import React, { useCallback, useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router";
import { Box } from "../../components/Layout/Box";
import { Content } from "../../components/Layout/Content";
import { LogoLoader } from "../../components/Loaders";
import { Media, breakpoints } from "../../styles";
import { ButtonSecondary } from "@/components/Buttons/Button";
import { LeaderboardTable } from "../Leaderboard/LeaderboardTable/LeaderboardTable";
import { SearchIcon } from "@/components/Icons/SearchIcon";
import { FilterSeasonDropdown } from "../Points/components/FilterSeasonDropdown";
import { POINTS_API, EXPLORER_API, HISTORY_API, POOL_API } from "@/constants/urls";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { formatDisplay, formatDisplayShorthand } from "@/utils/formatDisplay";
import { nftCollections, tokenMap } from "@/placeholders/tokens";
import {
  addTransaction,
  addTransactionCount,
  clearUserTransactions,
} from "@/store/transactionsSlice";
import { TransactionTable } from "../../components/Table/TransactionTable/TransactionTable";
import { updateMetrics } from "@/store/metricsSlice";
import { ethers } from "ethers";
import { useWidthBreakpoint } from "@/hooks";
import { TransactionTale } from "@/components/Table/TransactionTable/TransactionTale";
import { assetFilters, dateFilters } from "../../types/ChartTypes";
import { TVLChart } from "./TVLChart";
import { reduceString } from "@/utils/reduceString";
import { scrollbar } from "@/styles/scrollbar";
import { useSearch } from "@/hooks/useSearch";
import { RankComponent } from "../Wallet/Wallet";
import { queryLeaderboard } from "../Leaderboard/queryLeaderboard";
import { FeaturedLPTokens } from "../Pools/FeaturedLPTokens";
import { tokenColors } from "@/constants/tokenColors";
import { addPool } from "@/store/poolsSlice";
import { buildTransactions } from "../Transactions/TransactionHistory";

export const ExploreScreen = () => {
  const navigate = useNavigate();
  const isTablet = useWidthBreakpoint(breakpoints.tablet);
  const isMobile = useWidthBreakpoint(breakpoints.mobile);

  const [isLoading, setIsLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>({})
  const [transactions, setTransactionData] = useState<any[]>([]);
  const [featuredPools, setFeaturedPools] = useState<any[]>([]);

  const [selectedAssetsFilter, setSelectedAssetsFilter] = useState<{ label: string; }>({ label: "All Assets" });
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>("All");

  const [searchValue, setSearchValue] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<{address: string, rank: number}[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const leaderboardMap = leaderboard.reduce((acc, entry) => {
    acc[entry.address] = entry.id;
    return acc;
  }, {});
  
  const dispatch = useAppDispatch();
  const cachedLeaderboards = useAppSelector((state) => state.leaderboards.leaderboards);
  const cachedMetrics = useAppSelector((state) => state.metrics.data);
  const poolStats = useAppSelector((state) => state.pools.pools);

  let searchableTokenProperties = ["address"];
  if (!searchValue.startsWith("0x") && !isNaN(Number(searchValue))) searchableTokenProperties = ["id"];
  const searchResults = useSearch(leaderboard, searchableTokenProperties, searchValue);
  
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value.trim();
    setSearchValue(value);

    if (value === '' || value === '0' || value === '0x') {
        setSearchSuggestions([]);
    } else {
        const suggestions = searchResults.map(item => {
          const rank = leaderboardMap[item.address];
          return {
            address: item.address,
            rank: rank ? rank : -1
          };
        }).slice(0, 5);
        setSearchSuggestions(suggestions);
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case "ArrowUp":
        setSelectedIndex((prevIndex) => {
          if (typeof prevIndex !== "number") {
            return null;
          }
          return Math.max(0, prevIndex - 1);
        });
        break;
      case "ArrowDown":
        setSelectedIndex((prevIndex) => {
          if (typeof prevIndex !== "number") {
            return 0;
          }
          return Math.min(searchSuggestions.length - 1, prevIndex + 1);
        });
        break;
      case "Enter":
        if (typeof selectedIndex === "number" && selectedIndex >= 0 && selectedIndex < searchSuggestions.length) {
          const selectedSuggestion = searchSuggestions[selectedIndex].address;
          handleSearchEnter(selectedSuggestion);
        } else {
          handleSearchEnter();
        }
        break;
      default:
        break;
    }

    setTimeout(() => {
      if (dropdownRef.current && typeof selectedIndex === 'number') {
          const dropdown = dropdownRef.current;
          const selectedItem = suggestionRefs.current[selectedIndex];
  
          if (!selectedItem) return;
  
          const style = window.getComputedStyle(selectedItem);
          const marginTop = parseInt(style.marginTop, 10) || 0;
          const marginBottom = parseInt(style.marginBottom, 10) || 0;
  
          const itemTop = selectedItem.offsetTop - marginTop;
          const itemBottom = itemTop + selectedItem.offsetHeight + marginBottom;
  
          if (itemBottom > dropdown.scrollTop + dropdown.offsetHeight) {
              dropdown.scrollTop = itemBottom - dropdown.offsetHeight;
          } 
          else if (itemTop < dropdown.scrollTop) {
              dropdown.scrollTop = itemTop;
          }
          else if (selectedIndex === 0) {
              dropdown.scrollTop = 0;
          }
          else if (selectedIndex === searchSuggestions.length - 1) {
              dropdown.scrollTop = dropdown.scrollHeight - dropdown.offsetHeight;
          }
      }
    });
  };

  const handleSearchEnter = useCallback((suggestionValue?: string) => {
    const valueToSearch = suggestionValue || searchValue;
    if (!valueToSearch.trim()) return;

    if (!valueToSearch.startsWith("0x")) { // Rank search
      const rank = parseInt(valueToSearch.slice(1));
      const entry = leaderboard.find(entry => entry.id === rank);
      if (entry) {
        navigate(`/wallet/${entry?.address}`, { state: { rank: entry?.id } });
        return;
      }
    }

    if (ethers.utils.isAddress(valueToSearch)) { // Wallet search
      const entry = leaderboard.find(entry => entry.address === valueToSearch);
      navigate(`/wallet/${valueToSearch}`, { state: { rank: entry?.id } });
      return;
    }
    
    navigate(`/tx/${valueToSearch}`); // Default: Tx hash search
  }, [searchValue, navigate]);

  const queryMetrics = async () => {
    if (Object.keys(cachedMetrics).length > 0) return cachedMetrics;
    const data = await fetch(EXPLORER_API).then((response) => response.json());
    dispatch(updateMetrics({ data: data }));
    return data
  };

  const queryPools = async() => {

    const lpTokens : any[] = [tokenMap['wstETH+ETH'], tokenMap['ETH+USD'], tokenMap['Stablepool']]

    const fetchPoolInfo = async (poolName : string) => {
      if(poolStats[poolName])
          return poolStats[poolName]
  
      return fetch(POOL_API + poolName)
        .then((response) => response.json())
        .then((data) => {
          dispatch(addPool({ name: poolName, data: data }));
          return data;
        })
        .catch(() => {});
    };

    const results = await Promise.all(lpTokens.map((lpToken) => fetchPoolInfo(lpToken.name)))

    return results.map((poolInfo : any, index : number) => {
        const lpToken = lpTokens[index]
        return {
            icon: lpToken.icon,
            title: lpToken.name,
            description: "LP " + reduceString(lpToken.oceanID!, 6, 4),
            color: tokenColors[lpToken.tokens[0]],
            info: [
              {
                title: "APY",
                value: `${poolInfo.apy > 1 ? formatDisplay(((poolInfo.apy - 1) * 100).toString(), 2) : '--'}%`,
                color: "#7ADEB9",
              },
              {
                title: "Liquidity",
                value: `$${formatDisplayShorthand(poolInfo.totalValueLocked)}`,
              },
              {
                title: "24h Vol",
                value: `$${formatDisplayShorthand(poolInfo['24HrVolume'])}`,
              }
            ],
          }
    })

  }

  const queryTransactions = async () => {
    const apiData = await fetch(
      `${HISTORY_API}address/all/0`
    ).then((response) => response.json());

    const transactionData: any = apiData.transactions.data;
    const transactionHashes: string[] = Object.keys(transactionData);

    dispatch(
      addTransactionCount({ address: "all", count: apiData.transactions.count })
    );
    dispatch(clearUserTransactions({ user: "global" }));

    const nftIDs = new Set(
      nftCollections.map((collection) => collection.symbol)
    );

    const txs = buildTransactions(transactionData, transactionHashes)

    dispatch(addTransaction({ address: "all", skip: 0, data: txs }));
    return txs.slice(0, 4);
  };

  const queryData = async () => {
    await Promise.all([
      queryLeaderboard(dispatch, cachedLeaderboards),
      queryMetrics(),
      queryTransactions(),
      queryPools()
    ]).then(([leaderboardResult, metricsResult, txResult, poolResult]) => {
      setLeaderboard(leaderboardResult);
      setMetrics(metricsResult)
      setTransactionData(txResult);
      setFeaturedPools(poolResult)
    });
  };

  useEffect(() => {
    if (searchValue && searchValue !== '0' && searchValue !== '0x') {
        const suggestions = searchResults.map(item => ({
            address: item.address,
            rank: leaderboardMap[item.address] || -1
        })).slice(0, 5);
        setSearchSuggestions(suggestions);
    } else {
        setSearchSuggestions([]);
    }
}, [searchValue, searchResults.length, leaderboardMap.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchSuggestions([]);
      }    
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => { document.removeEventListener("mousedown", handleClickOutside); };
}, []);

  useEffect(() => {
    let _isMounted = true;
    if (_isMounted) {
      setIsLoading(true);
    }
    queryData()
      .then(() => {
        if (_isMounted) {
          setIsLoading(false);
        }
      })
      .catch((_) => {
        if (_isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      _isMounted = false;
    };
  }, []);

  return (
    <Content>
      {isLoading ? (
        <LoadingWrapper>
          <LogoLoader />
        </LoadingWrapper>
      ) : (
        <View>
          <SearchInputContainer ref={searchContainerRef}>
            <SearchInput
              value={searchValue}
              onChange={(event) => {handleSearchInputChange(event)}}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by txn hash / wallet address / rank #"
            />
            {searchSuggestions.length > 0 && (
            <SearchSuggestionsContainer itemCount={searchSuggestions.length}>
              <SearchSuggestionsDropdown itemCount={searchSuggestions.length} ref={dropdownRef}>
                {searchSuggestions.map((suggestion, index) => (
                  <SuggestionItem
                    ref={(el) => suggestionRefs.current[index] = el}
                    key={suggestion.rank} 
                    onClick={() => {
                      setSearchValue(suggestion.address);
                      setSearchSuggestions([]);
                      handleSearchEnter(suggestion.address);
                    }}
                    style={selectedIndex === index ? { backgroundColor: "#292941" } : {}}
                  >
                    {isTablet ? reduceString(suggestion.address, 12, 10) : suggestion.address}
                    <RankComponent rank={suggestion.rank} height="0.5rem" fontSize="12px" padding="10px"/>
                  </SuggestionItem>
                ))}
              </SearchSuggestionsDropdown>
            </SearchSuggestionsContainer>
            )}
            <SearchIconContainer onClick={() => handleSearchEnter()}>
              <SearchIcon />
            </SearchIconContainer>
          </SearchInputContainer>
          <Boxes>
            <SingleBox>
              <div>Total Value Locked</div>
                ${formatDisplay(metrics.totalValueLocked?.value.toString(), 0)}
              <div>
                Last 24h: <DailyChange value={metrics.totalValueLocked?.dailyChange}>{`${metrics.totalValueLocked?.dailyChange >= 0 ? '+' : '-'}$${formatDisplay(Math.abs(metrics.totalValueLocked?.dailyChange).toString(), 0)}`}</DailyChange>
              </div>
            </SingleBox>
            <SingleBox>
              <div>Lifetime Volume</div>
                ${formatDisplay(metrics.volume?.value.toString(), 0)}
              <div>
                Last 24h: <span>${formatDisplay(metrics.volume?.dailyChange.toString(), 0)}</span>
              </div>
            </SingleBox>
            <SingleBox>
              <div>Users</div>
                {formatDisplay(metrics.users?.value.toString())}
              <div>
                Last 24h: <span>{formatDisplay(metrics.users?.dailyChange.toString())}</span>
              </div>
            </SingleBox>
          </Boxes>
          {metrics.tvlChart && (
            <TVLChart
              title="Ocean Tokens Locked"
              poolTVL={false}
              lockedAssets={metrics.tvlChart}
            />
          )}
          <TableContainer>
            <TableHeader>
              <TableTitle>Pools</TableTitle>
              <TableButton
                onClick={() =>
                  navigate({
                    pathname: `/pools`,
                  })
                }
              >
                {(isMobile ? "" : "View ") + "More"}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_3983_174)">
                    <path
                      d="M13.1727 11.9997L8.22266 7.04974L9.63666 5.63574L16.0007 11.9997L9.63666 18.3637L8.22266 16.9497L13.1727 11.9997Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3983_174">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </TableButton>
            </TableHeader>
            <Divider />
            <FeaturedLPTokens boxes={featuredPools} />
          </TableContainer>
          {leaderboard.length > 0 && (
            <TableContainer>
              <TableHeader>
                <TableTitle>Leaderboard</TableTitle>
                <TableButton
                  onClick={() =>
                    navigate({
                      pathname: `/leaderboard`,
                    })
                  }
                >
                  {(isMobile ? "" : "View ") + "More"}
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_3983_174)">
                      <path
                        d="M13.1727 11.9997L8.22266 7.04974L9.63666 5.63574L16.0007 11.9997L9.63666 18.3637L8.22266 16.9497L13.1727 11.9997Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3983_174">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </TableButton>
              </TableHeader>
              <Divider />
              <LeaderboardTable transactions={leaderboard.slice(0, 3)} />
            </TableContainer>
          )}
          {transactions.length > 0 && (
            <TableContainer>
              <TableHeader>
                <TableTitle>Latest Transactions</TableTitle>
                <TableButton
                  onClick={() =>
                    navigate({
                      pathname: `/history/all`,
                    })
                  }
                >
                  {(isMobile ? "" : "View ") + "More"}
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_3983_174)">
                      <path
                        d="M13.1727 11.9997L8.22266 7.04974L9.63666 5.63574L16.0007 11.9997L9.63666 18.3637L8.22266 16.9497L13.1727 11.9997Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3983_174">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </TableButton>
              </TableHeader>
              <Divider />
              {isTablet ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {transactions.map((transaction, index) => (
                    <TransactionTale key={transaction.transactionHash} transaction={transaction} />
                  ))}
                </div>
              ) : (
                <TransactionTable data={transactions} />
              )}
            </TableContainer>
          )}
        </View>
      )}
    </Content>
  );
};

const SearchIconContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  box-sizing: border-box;
  border: 2px solid rgb(55, 220, 242);
`;

const SearchInputContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  border: 1px solid #171b33;
  padding: 8px;
  padding-left: 16px;
  border-radius: 16px;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.3s;

  &:focus,
  &:hover,
  &:active,
  &:focus-within {
    border-color: rgb(55, 220, 242);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: white;
  font-size: 16px;
  line-height: 19.36px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: #7d7d97;
  }

  ${Media.tablet} {
    font-size: 12px;
  }
`;

const SearchSuggestionsContainer = styled.div<{ itemCount: number }>`
  position: absolute;
  top: 100%;  
  left: 0;
  width: 100%;
  padding-right: 8px;
  z-index: 1000;
  background-color: #171b33;
  border-radius: 16px;
  margin-top: 10px;
  box-sizing: border-box;
  overflow: hidden;
  height: ${({ itemCount }) => (itemCount > 3 ? '212px' : 'auto')};
  box-shadow: rgb(10, 14, 39) 0rem 3rem 12rem 6px;
`;

const SearchSuggestionsDropdown = styled.div<{ itemCount: number }>`
  padding-left: 0.5rem;
  padding-right: ${({ itemCount }) => (itemCount > 3 ? '0.5rem' : '0')};
  overflow-x: hidden;
  max-height: 100%;
  overflow-y: ${({ itemCount }) => (itemCount > 3 ? 'auto' : 'hidden')};  
  ${scrollbar({ border: "16px", width: "6px", marginTop: "12px", marginBottom: "12px", backgroundColor: "#171b33", scrollbarColor: "#2C5173" })};
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  color: white;
  transition: background-color 0.3s;
  border-radius: 16px;
  margin: 12px 0;
  gap: 10px;

  &:hover {
    background-color: #292941;
  }

  ${Media.tablet} {
    padding: 12px;
    font-size: 12px;
  }
`;

const View = styled(Box)`
  padding: 20px 20px 20px;
  display: flex;
  flex-direction: column;

  ${Media.tablet} {
    padding: 8px 8px 12px;
  }
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  vertical-align: center;
  width: 100%;
  padding-top: 300px;
  padding-bottom: 300px;
`;

const TableContainer = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px;
  border: 1px solid #171b33;
  border-radius: 20px;
  background-color: #0a0e27;

  ${Media.tablet} {
    margin-top: 16px;
    padding: 0px;
    border: none;
    margin-top: 16px;
  }
`;

const TableHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${Media.tablet} {
    align-items: center;
    gap: 16px;
  }
`;

const TableTitle = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: -0.03em;
  color: #ffffff;
  ${Media.tablet} {
    font-size: 20px;
    padding-left: 8px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #171b33;
  margin-block: 16px;

  ${Media.tablet} {
    margin-block: 12px 16px;
  }
`;

const TableButton = styled(ButtonSecondary)`
  width: fit-content;
  height: 48px;
  border-width: 2px;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  padding-inline: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 16px;

  svg {
    transition: all 0.3s;
  }

  &:hover {
    svg {
      transform: translateX(5px);
    }
  }

  ${Media.tablet} {
    width: 30%;
    height: 40px;
    border-radius: 20px;
    font-size: 14px;
    padding-inline: 8px 0px;
    gap: 0px;
  }
`;

const Boxes = styled.div`
  display: flex;
  gap: 8px;
  margin-block: 16px 20px;

  ${Media.tablet} {
    flex-direction: column;
    margin-block: 0;
    margin-bottom: 16px;
  }
`;

const SingleBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  border-radius: 16px;
  background: #151530;
  border: 1px solid #1e2239;
  font-size: 30px;
  line-height: 0.85em;
  font-weight: 500;
  flex: 1;

  div {
    color: #7d7d97;
    font-weight: 400;
    font-size: 16px;
  }

  span {
    color: #ffffff;
    font-weight: 500;
  }
  ${Media.tablet} {
    font-size: 24px;

    div {
      font-size: 14px;
    }
  }
`;

const DailyChange = styled.span<{ value: number }>`
  ${({ value }) =>
    value > 0 &&
    css`
      color: #7adeb9 !important;
    `};
  ${({ value }) =>
    value < 0 &&
    css`
      color: #ff5349 !important;
    `};
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;

  ${Media.tablet} {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;

  ${Media.tablet} {
    align-items: center;
    gap: 8px;
  }
`;

const DateFiltersContainer = styled.div`
  display: flex;
  gap: 6px;
`;

const DateFilterButton = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 57px;
  height: 32px;
  background: #1e2239;
  border: 1px solid #292941;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  cursor: pointer;
  transition: all 0.3s;
  color: ${({ active }) => (active ? "#FFFFFF" : "#7D7D97")};

  &:hover {
    color: #ffffff;
  }

  ${Media.tablet} {
    width: 42px;
    height: 28px;
  }
`;
